// Silent auto-onboarding hook for Charlie
// Detects first-time users and automatically creates their personal workspace + Teable space

import { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '@foundation/altzero_base/base/contextapi/UserContext';
import { useOnboardingStatus } from '@foundation/altzero_base/base/hooks/useOnboardingStatus';
import { supabase } from '@foundation/altzero_base/base/utils/supabaseClient';
import { triggerTeableOnboarding } from '@foundation/altzero_base/base/utils/teableOnboarding';

type AutoOnboardingState = 'idle' | 'checking' | 'onboarding' | 'complete' | 'error';

type AutoOnboardingResult = {
  state: AutoOnboardingState;
  isReady: boolean;
  error: string | null;
};

const ONBOARDING_LOCK_KEY = 'charlie_auto_onboarding_lock';
const LOCK_TTL_MS = 120000;

const deriveWorkspaceName = (user: {
  email?: string;
  user_metadata?: Record<string, unknown>;
}): string => {
  const rawName = user.user_metadata?.full_name;
  const fullName = typeof rawName === 'string' ? rawName.trim() : undefined;
  if (fullName && fullName.length >= 2) {
    return `${fullName}'s Space`;
  }
  const emailPrefix = user.email?.split('@')[0] || 'My';
  return `${emailPrefix}'s Space`;
};

const getFullNameFromMetadata = (
  metadata?: Record<string, unknown>
): string => {
  const raw = metadata?.full_name;
  return typeof raw === 'string' ? raw : '';
};

export const useAutoOnboarding = (): AutoOnboardingResult => {
  const { user } = useUser();
  const {
    isOnboarded,
    needsOnboarding,
    isLoading: statusLoading,
    hasWorkspace,
    refresh,
  } = useOnboardingStatus();
  const [state, setState] = useState<AutoOnboardingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const onboardingAttempted = useRef(false);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const stableRefresh = useCallback(() => {
    refreshRef.current();
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!user?.id || statusLoading) {
      setState('checking');
      return;
    }

    if (isOnboarded && hasWorkspace) {
      setState('complete');
      return;
    }

    if (onboardingAttempted.current) {
      return;
    }

    // Prevent duplicate onboarding across tabs
    const lockValue = localStorage.getItem(ONBOARDING_LOCK_KEY);
    if (lockValue) {
      const lockTime = parseInt(lockValue, 10);
      if (Date.now() - lockTime < LOCK_TTL_MS) {
        setState('onboarding');
        const pollInterval = setInterval(() => {
          stableRefresh();
        }, 3000);
        const timeout = setTimeout(() => {
          clearInterval(pollInterval);
          localStorage.removeItem(ONBOARDING_LOCK_KEY);
          if (!cancelled) setState('complete');
        }, LOCK_TTL_MS);
        return () => {
          cancelled = true;
          clearInterval(pollInterval);
          clearTimeout(timeout);
        };
      }
      localStorage.removeItem(ONBOARDING_LOCK_KEY);
    }

    if (!needsOnboarding) {
      return;
    }

    onboardingAttempted.current = true;

    const performSilentOnboarding = async () => {
      if (!user?.id) return;

      setState('onboarding');
      setError(null);
      localStorage.setItem(ONBOARDING_LOCK_KEY, Date.now().toString());

      try {
        const workspaceName = deriveWorkspaceName(user);

        const { data: result, error: rpcError } = await supabase.rpc(
          'complete_onboarding_with_workspace',
          {
            p_user_id: user.id,
            p_workspace_name: workspaceName,
            p_workspace_description: 'Auto-created personal workspace',
            p_workspace_type: 'personal',
            p_profile_data: {
              full_name: getFullNameFromMetadata(user.user_metadata),
            },
          }
        );

        if (rpcError) {
          throw rpcError;
        }

        // Trigger Teable space creation in background (non-blocking)
        if (result?.tenant_id) {
          triggerTeableOnboarding(
            result.tenant_id,
            workspaceName,
            false
          ).catch((teableError) => {
            console.error(
              'Teable onboarding failed (non-blocking):',
              teableError
            );
          });
        }

        if (!cancelled) {
          setState('complete');
          stableRefresh();
        }
      } catch (err) {
        console.error('Silent auto-onboarding failed:', err);
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Auto-onboarding failed'
          );
          // Don't block the user on failure
          setState('complete');
        }
      } finally {
        localStorage.removeItem(ONBOARDING_LOCK_KEY);
      }
    };

    performSilentOnboarding();

    return () => {
      cancelled = true;
    };
  }, [user?.id, statusLoading, isOnboarded, needsOnboarding, hasWorkspace, stableRefresh]);

  const isReady = state === 'complete' || state === 'error';

  return { state, isReady, error };
};
